/**
 * KB-3306 — the vendor-disagreement hint, rendered under a governed field's input (staff only).
 *
 * Shows only when the pipeline value differs from canonical and has not been rejected
 * (`shouldShowVendorHint`, AC3). The state doc carries only the pipeline value's hash, so the value is
 * read lazily from `fieldContributions/<latestPipeline.cid>`. "Use vendor value" sets the form value
 * (via `onUseVendorValue`, no callable — the trigger attributes the save, the next run converges the
 * basis); "Keep mine" calls the reject callable. Hidden for directors in this ticket (they can read
 * neither contributions nor the org flag — that is KB-3318). "Use vendor value" is itself hidden where
 * the form has no single input for the value (`canUseVendorValue`).
 */
import React, { useEffect, useState } from "react";
import { format } from "date-fns";

import { toast } from "@/components/ui/sonner";
import {
  canUseVendorValue,
  fetchFieldContribution,
  setCampDataFieldPolicy,
  shouldShowVendorHint,
  useFieldState,
  type BadgeAudience,
} from "@/services/campDataFieldState";

interface FieldVendorHintProps {
  fieldPath: string;
  audience?: BadgeAudience;
  /** Called with the vendor value when the operator picks "Use vendor value" — wire to the field's onChange. */
  onUseVendorValue?: (value: unknown) => void;
}

function displayValue(v: unknown): string {
  if (v == null) return "(empty)";
  if (typeof v === "string") return v.length > 140 ? `${v.slice(0, 140)}…` : v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  try {
    const s = JSON.stringify(v);
    return s.length > 140 ? `${s.slice(0, 140)}…` : s;
  } catch {
    return "(complex value)";
  }
}
function fmtRunDate(iso?: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "" : format(d, "MMM d, yyyy");
}

export const FieldVendorHint: React.FC<FieldVendorHintProps> = ({ fieldPath, audience: audienceProp, onUseVendorValue }) => {
  const { record, entity, reload, audience: ctxAudience, hint } = useFieldState(fieldPath);
  // The audience is the provider's (a mount passes no prop; the welcome flow's provider is "director").
  // The prop is an override kept for standalone rendering/tests. Default "staff" preserves prior behaviour.
  const audience = audienceProp ?? ctxAudience ?? "staff";
  const [value, setValue] = useState<unknown>(undefined);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);

  const eligible = audience === "staff" && shouldShowVendorHint(record);
  const cid = eligible ? record!.latestPipeline!.cid : undefined;

  useEffect(() => {
    if (!cid) {
      setLoaded(false);
      setValue(undefined);
      return;
    }
    let mounted = true;
    fetchFieldContribution(cid)
      .then((row) => {
        if (!mounted) return;
        if (!row) {
          // A stale latestPipeline.cid pointing at a missing contribution doc: HIDE, rather than render a
          // confident "Vendor currently says (empty)" and a "Use vendor value" that would write undefined
          // into the form. Never render an absence we did not verify (Rule 4). A row that exists with a
          // genuinely empty value (null / "") still shows — that is a real vendor value.
          setLoaded(false);
          setValue(undefined);
          return;
        }
        setValue(row.value);
        setLoaded(true);
      })
      .catch(() => {
        if (mounted) setLoaded(false); // a failed read hides the hint rather than showing a broken one
      });
    return () => {
      mounted = false;
    };
  }, [cid]);

  // ── DIRECTOR branch (KB-3487) ────────────────────────────────────────────────────────────────────
  // A director cannot read `fieldContributions` (admin-only), so the value arrives already redacted from
  // the `getCampDataFieldHints` callable, fetched ONCE per form by the provider (ruling 6) and exposed as
  // `hint`. No "Use vendor value" / "Keep mine": `setCampDataFieldPolicy` is staff-only. The copy is "first
  // showed", never "now" — `firstSeenAt` is the first observation, not the current state (ticket §3).
  if (audience === "director") {
    if (!hint) return null; // no hint (switch off / no vendor disagreement / denied) → render nothing.
    const runDate = fmtRunDate(hint.firstSeenAt);
    return (
      <p className="text-sm text-muted-foreground mt-1" data-testid="field-vendor-hint-director" data-field-path={fieldPath}>
        Your website first showed <span className="font-medium text-foreground">{displayValue(hint.value)}</span>
        {runDate ? ` on ${runDate}` : ""}
      </p>
    );
  }

  if (!eligible || !loaded || !record) return null;

  const lp = record.latestPipeline!;
  const runDate = fmtRunDate(lp.observedAt);
  const showUse = canUseVendorValue(fieldPath) && !!onUseVendorValue;

  async function keepMine() {
    if (!entity) return;
    setBusy(true);
    try {
      await setCampDataFieldPolicy({ entity, fieldPath, action: "reject", valueHash: lp.valueHash });
      toast.success("Kept your value — this vendor value won't be proposed again");
      reload();
    } catch {
      toast.error("Could not record your choice");
    } finally {
      setBusy(false);
    }
  }

  return (
    <p className="text-sm text-muted-foreground mt-1" data-testid="field-vendor-hint" data-field-path={fieldPath}>
      <span>
        Vendor currently says <span className="font-medium text-foreground">{displayValue(value)}</span>
        {runDate ? ` (run ${runDate})` : ""} ·{" "}
      </span>
      {showUse && (
        <button
          type="button"
          className="underline underline-offset-2 hover:text-foreground disabled:opacity-50"
          disabled={busy}
          onClick={() => onUseVendorValue?.(value)}
        >
          Use vendor value
        </button>
      )}
      {showUse && <span> </span>}
      <button
        type="button"
        className="underline underline-offset-2 hover:text-foreground disabled:opacity-50"
        disabled={busy}
        onClick={keepMine}
      >
        Keep mine
      </button>
    </p>
  );
};

export default FieldVendorHint;
